import * as bootstrap from 'bootstrap';
import $ from 'jquery';

import { compareVersions } from './utils';

const S3_FIRMWARE = 'https://ab-net-firmware.s3.amazonaws.com/firmware';
export const FIRMWARE_PRODUCTION_TARGET = '04.13.40';

// Known SM5KE firmware versions — used for test-mode bouncing (upgrade ↔ downgrade).
// Add entries here as new firmware is released or old versions are needed for testing.
const FIRMWARE_KNOWN_VERSIONS = {
  '04.11.40': `${S3_FIRMWARE}/SH7E_SM5KE_UH5E_WP400_04.11.40.epk`,
  '04.13.40': `${S3_FIRMWARE}/SH7E_SM5KE_UH5E_WP400_04.13.40.epk`,
};

export function getFirmwareTarget(deviceVersion) {
  if (localStorage.getItem('firmwareTestMode') === '1') {
    // Test mode: offer whichever known version the device isn't currently on.
    // Enables clean bounce: run on old → offer new, run on new → offer old.
    const other = Object.keys(FIRMWARE_KNOWN_VERSIONS).find(v => v !== deviceVersion);
    return other ? { version: other, path: FIRMWARE_KNOWN_VERSIONS[other] } : null;
  }
  // Production: upgrade only, to the current target
  if (compareVersions(deviceVersion || '0.0.0', FIRMWARE_PRODUCTION_TARGET) < 0) {
    return { version: FIRMWARE_PRODUCTION_TARGET, path: FIRMWARE_KNOWN_VERSIONS[FIRMWARE_PRODUCTION_TARGET] };
  }
  return null;
}

/**
 * Opens a firmware progress modal and polls DynamoDB (via refreshDynamo) for
 * status updates that the device sends in its regular pings during the update.
 *
 * @param {object} row - The device row data
 * @param {Function} refreshDynamo - Async fn that re-fetches DynamoDB and returns
 *   the updated row for the given macAddress, or null if not found
 * @returns {{ onClose: (fn: Function) => void }} - register a callback for when modal closes
 */
export function firmwareProgress(row, refreshDynamo) {
  const name = row.wall?.length ? row.display_name : row.device_name;
  const allDevices = [row, ...(row.wall || [])];
  const versions = [...new Set(allDevices.map(d => d.firmwareVersion || '0.0.0'))];
  const target = versions.length === 1 ? getFirmwareTarget(versions[0]) : null;
  const targetVersion = target?.version ?? FIRMWARE_PRODUCTION_TARGET;

  $('body').append(`
    <div class="modal fade" id="firmwareprogressmodal" tabindex="-1" aria-hidden="true" data-bs-backdrop="static">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Firmware Update</h5>
          </div>
          <div class="modal-body">
            <p>Updating <span class="bold">${name}</span> to firmware <span class="bold">${targetVersion}</span></p>
            <p id="fw-phase-text" class="mb-1">Sending command to display…</p>
            <div class="progress mb-2" style="height: 22px;">
              <div id="fw-progress-bar"
                   class="progress-bar progress-bar-striped progress-bar-animated bg-primary"
                   role="progressbar"
                   style="width: 0%"
                   aria-valuenow="0" aria-valuemin="0" aria-valuemax="100">
                0%
              </div>
            </div>
            <p class="text-muted small mb-0" id="fw-status-detail">The device will reboot automatically when the upgrade is complete.</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Dismiss</button>
          </div>
        </div>
      </div>
    </div>
  `);

  const progressModal = new bootstrap.Modal('#firmwareprogressmodal');
  const modalElement = document.getElementById('firmwareprogressmodal');

  let pollInterval = null;
  let closeCallbacks = [];

  function setProgress(pct, phaseText, detailText) {
    const bar = document.getElementById('fw-progress-bar');
    const phase = document.getElementById('fw-phase-text');
    const detail = document.getElementById('fw-status-detail');
    if (!bar) return;
    const clamped = Math.min(100, Math.max(0, pct));
    bar.style.width = `${clamped}%`;
    bar.setAttribute('aria-valuenow', clamped);
    bar.textContent = `${clamped}%`;
    if (phaseText && phase) phase.textContent = phaseText;
    if (detailText && detail) detail.textContent = detailText;
  }

  async function poll() {
    const updatedRow = await refreshDynamo(row.macAddress, row.client);
    if (!updatedRow) return; // Dynamo fetch failed — skip this tick

    // Aggregate across all screens in the wall — always show the least-advanced device.
    // Each screen writes its own firmwarePhase/firmwareProgress via sendDeviceInfo().
    // Real LG SCAP sequence: 'in progress'(init) → 'downloading' → 'ready' → 'installing'
    // 'installing' is our renamed label for the second 'in progress' (downloadProgress≥100).
    const allScreens = [updatedRow, ...(updatedRow.wall || [])];
    const PHASE_RANK = { 'in progress': 0, downloading: 1, ready: 2, installing: 3 };
    const activeScreens = allScreens.filter(d => d.firmwarePhase in PHASE_RANK);
    const phase = activeScreens.length
      ? activeScreens.reduce((worst, d) => (PHASE_RANK[d.firmwarePhase] < PHASE_RANK[worst.firmwarePhase] ? d : worst))
          .firmwarePhase
      : updatedRow.firmwarePhase;
    const progress = activeScreens.length
      ? Math.min(...activeScreens.map(d => parseInt(d.firmwareProgress ?? '0', 10) || 0))
      : parseInt(updatedRow.firmwareProgress ?? '0', 10) || 0;
    // Version check first — if all screens already report the target version the update
    // is done even if DynamoDB still has stale firmwarePhase from before the reboot.
    if (allScreens.every(d => d.firmwareVersion === targetVersion)) {
      clearInterval(pollInterval);
      pollInterval = null;
      setProgress(100, 'Firmware update complete!', 'The display has rebooted with the new firmware.');
      const bar = document.getElementById('fw-progress-bar');
      if (bar) {
        bar.classList.remove('progress-bar-animated', 'bg-primary', 'bg-warning');
        bar.classList.add('bg-success');
      }
      return;
    }

    if (phase === 'in progress') {
      setProgress(0, 'Initializing firmware update…', 'Connecting to server…');
    } else if (phase === 'downloading') {
      setProgress(progress, 'Downloading firmware…', `Download progress: ${progress}%`);
    } else if (phase === 'ready') {
      setProgress(100, 'Download complete — preparing to install…', 'Waiting to begin firmware flash.');
    } else if (phase === 'installing') {
      setProgress(progress || 1, 'Installing firmware…', `Install progress: ${progress}%`);
      const bar = document.getElementById('fw-progress-bar');
      if (bar) bar.classList.replace('bg-primary', 'bg-warning');
    }
    // else: version unchanged and no active phase — device still rebooting, keep polling
  }

  function dispose() {
    if (pollInterval) clearInterval(pollInterval);
    pollInterval = null;
    progressModal.dispose();
    $('#firmwareprogressmodal').remove();
    closeCallbacks.forEach(fn => fn());
    closeCallbacks = [];
  }

  modalElement.addEventListener('hidden.bs.modal', dispose);

  progressModal.show();

  // Wait a moment before polling so the device has time to receive the command
  setTimeout(() => {
    poll();
    pollInterval = setInterval(poll, 6_000);
  }, 5_000);

  return {
    onClose: fn => closeCallbacks.push(fn),
  };
}

export default { firmwareProgress };
